import React, { useEffect, useRef, useCallback } from 'react';
import { View, Platform } from 'react-native';
import PostListItem from './PostListItem';
import { Post } from '../types';
import { useOptionalVideoContext } from '../contexts/VideoContext';

interface ObservablePostItemProps {
  post: Post;
}

// Global registry to track intersection ratios of all video posts
// This prevents race conditions when multiple videos are visible
const visibilityRegistry = new Map<string, number>();
let updateScheduled = false;
let setVisibleFn: ((id: string | null) => void) | null = null;

function scheduleVisibilityUpdate() {
  if (updateScheduled) return;
  updateScheduled = true;

  requestAnimationFrame(() => {
    updateScheduled = false;
    if (!setVisibleFn) return;

    // Find the post with the highest intersection ratio (above threshold)
    let bestId: string | null = null;
    let bestRatio = 0;

    visibilityRegistry.forEach((ratio, id) => {
      if (ratio >= 0.5 && ratio > bestRatio) {
        bestRatio = ratio;
        bestId = id;
      }
    });

    setVisibleFn(bestId);
  });
}

/**
 * Wrapper component for PostListItem that uses Intersection Observer
 * to detect visibility and trigger video autoplay on web.
 */
export default function ObservablePostItem({ post }: ObservablePostItemProps) {
  const elementRef = useRef<HTMLElement | null>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);
  const videoContext = useOptionalVideoContext();

  // Keep the global setVisible function updated
  useEffect(() => {
    if (videoContext) {
      setVisibleFn = videoContext.setVisiblePostId;
    }
  }, [videoContext]);

  // Clean up registry on unmount
  useEffect(() => {
    return () => {
      if (post.video) {
        visibilityRegistry.delete(post.id);
        scheduleVisibilityUpdate();
      }
    };
  }, [post.id, post.video]);

  const setRef = useCallback((node: View | null) => {
    // Clean up previous observer
    if (observerRef.current) {
      observerRef.current.disconnect();
      observerRef.current = null;
    }

    // Only run on web and for posts with videos
    if (Platform.OS !== 'web' || !post.video || !videoContext || !node) {
      elementRef.current = null;
      return;
    }

    // Get the underlying DOM element
    const element = node as unknown as HTMLElement;
    elementRef.current = element;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          // Update this post's ratio in the global registry
          if (entry.isIntersecting) {
            visibilityRegistry.set(post.id, entry.intersectionRatio);
          } else {
            visibilityRegistry.delete(post.id);
          }
        });

        // Schedule a single update to pick the best candidate
        scheduleVisibilityUpdate();
      },
      {
        threshold: [0, 0.25, 0.5, 0.75, 1],
        rootMargin: '0px',
      }
    );

    observer.observe(element);
    observerRef.current = observer;
  }, [post.video, post.id, videoContext]);

  return (
    <View ref={setRef as any}>
      <PostListItem post={post} />
    </View>
  );
}
